import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { requestDoneMoment } from '@/src/renderer/lib/doneMoment'
import { rememberUndo } from '@/src/renderer/lib/undo'
import { retractSpawnedSuccessor, spawnNextOccurrence } from '../utils/spawnNextOccurrence'
import { dismissComebackToast, showComebackToast } from '../utils/comebackToast'
import { duplicateTask } from '../utils/duplicateTask'
import { useRowSubtaskTree } from '../components/TaskRowData'
import { showInfoToast } from '@/src/renderer/components/common/MessageToast'
import type { AttentionLevel, DurationMode, RecurrenceRule, StartPeriod, Task, TaskCascade } from '@/src/renderer/lib/db/models'

/** One checklist item, in the same shape whether it is stored or still buffered. */
export interface SubtaskItem {
  id: string
  title: string
  done: boolean
  /** Was die Zeile zusätzlich zeigt — nur gespeicherte Unteraufgaben haben es (T14 #179). */
  attention?: AttentionLevel | null
  startDate?: Date | null
  startTime?: string | null
  startPeriod?: StartPeriod | null
  dueDate?: Date | null
  dueTime?: string | null
  duePeriod?: StartPeriod | null
  /** Nur fürs **Aufgaben-Menü** an der Zeile (#205): Seine Kästen zeigen den gesetzten Wert. */
  estimatedDuration?: number | null
  durationMode?: DurationMode | null
  tags?: string[]
  /** Ruht selbst (#205): gedämpft, mit gestricheltem Haken. */
  pausedAt?: Date | null
  /** Eigene Belohnung (#205): Zeichen an der Zeile, Geschafft-Moment beim eigenen Abhaken. */
  rewardId?: string | null
  /** Eigene Wiederholung (#205): Zeichen an der Zeile, Nachfolger beim eigenen Abhaken. */
  recurrence?: RecurrenceRule | null
  /**
   * **Ebene im Baum** (#181): 1 steht direkt unter der Aufgabe, bis 4. Ein Entwurf kennt nur
   * Ebene 1 — ohne Angabe ebenso.
   */
  depth?: number
  /** Der Eltern-Teil — die Aufgabe selbst oder eine Zeile darüber (#181). */
  parentId?: string
  /** Die direkten Kinder, erledigt und gesamt — `total` 0 ohne Kinder (#211). */
  progress?: { done: number; total: number }
}

/** Was das **Aufgaben-Menü** an Unteraufgaben ändern kann (#205, Etappe 0). */
export type SubtaskChanges = Partial<Pick<Task,
  | 'startDate' | 'startTime' | 'startPeriod'
  | 'dueDate' | 'dueTime' | 'duePeriod'
  | 'estimatedDuration' | 'durationMode'
  | 'attention' | 'tags'
  | 'pausedAt' | 'rewardId' | 'recurrence'
>>

/** What a buffered checklist hands to `TaskCreationData`. */
export interface SubtaskDraft {
  title: string
  done: boolean
}

export interface SubtaskEditor {
  items: SubtaskItem[]
  /**
   * Ob die Liste wirklich schon da ist.
   *
   * Eine gespeicherte Aufgabe holt ihre Checkliste aus der Datenquelle, und bis die
   * Antwort da ist, sieht `items` aus wie eine leere Liste. Wer daran sein Layout
   * hängt, baut es zweimal — beim Aufbau leer, einen Augenblick später gefüllt. Ein
   * gepufferter Entwurf hat nichts zu laden und ist sofort geladen.
   */
  loaded: boolean
  /** Empty for a saved task — its checklist is already in the database. */
  drafts: SubtaskDraft[]
  /**
   * **Fortschritt der Aufgabe** (#181, Grill): nur die direkten Kinder, erledigt und gesamt — `items`
   * enthält den ganzen Baum.
   */
  progress: { done: number; total: number }
  add: (title: string) => Promise<void>
  /**
   * Legt eine Zeile direkt hinter `afterId` an, ohne die Nachbarn anzufassen,
   * und meldet deren id: Die **offene Zeile** rückt dahinter nach, damit die
   * Kette in der Mitte weiterläuft statt rückwärts zu bauen.
   */
  insertAfter: (afterId: string, title: string) => Promise<string | undefined>
  /** Schiebt eine Zeile hinter `afterId`; `null` heißt an den Anfang. */
  move: (id: string, afterId: string | null) => Promise<void>
  /**
   * Hakt ab oder zurück. **Mit Belohnung oder Wiederholung wie eine Aufgabe** (#205, #213): der
   * Geschafft-Moment, die nächste Runde samt „Kommt wieder …" und `⌘Z`; der Haken zurück nimmt
   * Runde und Toast wieder weg. `count`: wie viele zugleich abgehakt wurden — der Moment gilt nur
   * einer. Gibt die nächste Runde zurück.
   */
  toggle: (id: string, count?: number) => Promise<Task | null | void>
  rename: (id: string, title: string) => Promise<void>
  /** Entfernt eine Zeile und meldet zurück, was weg ist — der Stoff für das Rückgängig. */
  remove: (id: string) => Promise<SubtaskItem | undefined>
  /** Rückweg von `remove`: die Zeile kommt an ihren Platz zurück. */
  restore: (item: SubtaskItem) => Promise<void>
  /**
   * Schreibt dieselben Änderungen an mehrere Zeilen, alle oder keine — das **Aufgaben-Menü**
   * meint die ganze Auswahl. Ein Entwurf hat nichts zu schreiben: Seine Zeilen kennen nur
   * Titel und Haken.
   */
  update: (ids: string[], changes: SubtaskChanges) => Promise<void>
  /**
   * **Einrücken** (⌥→, #181): unter die Zeile darüber auf derselben Ebene; der Ast zieht mit.
   * `⌘Z` holt den alten Platz zurück. Geht es nicht, sagt ein Hinweis warum. `true`, wenn es ging.
   */
  indent: (ids: string[]) => Promise<boolean>
  /** **Ausrücken** (⌥←, #181): eine Ebene hinauf, hinter den Eltern-Teil. Ebene 1 bleibt still. */
  outdent: (ids: string[]) => Promise<boolean>
  /**
   * **Duplizieren** (⌘D, #209): Jede Kopie steht direkt unter ihrer Vorlage, mit allem, was sie
   * trägt — wie eine kopierte Aufgabe. Meldet die Kopien, damit die Liste sie anfasst. Ein
   * Entwurf kopiert nichts.
   */
  duplicate: (ids: string[]) => Promise<string[]>
}

/**
 * The checklist of one task, editable from both sides of the create/edit split.
 *
 * With a `taskId` every change goes to the data source (Dexie in the app)
 * immediately and syncs: ticking a
 * box is a deliberate act, not a draft, so it must survive Escape.
 *
 * Without one — the task is still being created and has no id yet — the same
 * operations run against an in-memory buffer, which `drafts` hands to the
 * creation payload. The rows are written once `db.createTask` returns an id.
 *
 * @example
 * const checklist = useSubtaskEditor({ taskId: task?.id })
 * await checklist.add('Prototyp bauen')
 */
export function useSubtaskEditor({ taskId, resetKey }: { taskId: string | undefined; resetKey?: number }): SubtaskEditor {
  const buffered = !taskId
  const [buffer, setBuffer] = useState<SubtaskItem[]>([])
  /*
    **Ein neues Anlegen beginnt leer** (gemeldet 04.10.2026, Bild 44): Die Schnelleingabe behält ihre Zeile und
    leert sie per `resetKey` — wie das Formular (`useTaskEntryForm`) auch den Puffer.
  */
  const prevResetKey = useRef(resetKey)
  if (prevResetKey.current !== resetKey) {
    prevResetKey.current = resetKey
    if (buffered && buffer.length > 0) setBuffer([])
  }
  const source = useDataSource()

  // Der ganze Baum in Vorordnung (#181) — die Checkliste zeichnet ihn eingerückt. In einer Liste aus deren Bündel (#303).
  const stored = useRowSubtaskTree(taskId)

  const items = useMemo<SubtaskItem[]>(() => {
    if (buffered) return buffer
    return (stored ?? []).map(s => ({
      id: s.id, title: s.title, done: s.done,
      attention: s.attention, startDate: s.startDate, startTime: s.startTime, startPeriod: s.startPeriod,
      dueDate: s.dueDate, dueTime: s.dueTime, duePeriod: s.duePeriod,
      estimatedDuration: s.estimatedDuration, durationMode: s.durationMode, tags: s.tags,
      pausedAt: s.pausedAt, rewardId: s.rewardId, recurrence: s.recurrence,
      depth: s.depth, parentId: s.taskId, progress: s.progress,
    }))
  }, [buffered, buffer, stored])

  const progress = useMemo(() => {
    const top = items.filter(item => (item.depth ?? 1) === 1)
    return { done: top.filter(item => item.done).length, total: top.length }
  }, [items])

  const add = useCallback(async (title: string) => {
    const trimmed = title.trim()
    if (!trimmed) return
    if (buffered) {
      setBuffer(prev => [...prev, { id: crypto.randomUUID(), title: trimmed, done: false }])
      return
    }
    await source.createSubtask(taskId!, trimmed)
  }, [buffered, taskId, source])

  const { t, i18n } = useTranslation()
  // `⌘Z` ruft den Haken erneut — ein `useCallback` kann sich nicht selbst in seinen Abhängigkeiten nennen.
  const toggleRef = useRef<(id: string) => Promise<unknown>>(async () => {})
  const toggle = useCallback(async (id: string, count = 1): Promise<Task | null | void> => {
    if (buffered) {
      setBuffer(prev => prev.map(s => (s.id === id ? { ...s, done: !s.done } : s)))
      return
    }
    const current = await source.getSubtask(id)
    if (!current) return
    const done = !current.done
    await source.updateSubtask(id, { done })
    // Der Haken zurück in derselben Sitzung nimmt die neue Runde und ihren Toast wieder weg.
    if (!done) {
      if (current.recurrence) await retractSpawnedSuccessor(source, id)
      dismissComebackToast(id)
      return
    }
    /*
      Mitgehakt über die Aufgabe (Kopplung) läuft nicht hier durch — das bleibt still, ohne nächste
      Runde. Gemerkt wird erst nach dem Schreiben: Jeder Schreibvorgang lässt die gemerkte Handlung
      vergessen (ADR 0028).
    */
    if (!current.rewardId && !current.recurrence) return
    const task = await source.getTask(id)
    if (!task) return
    const successor = current.recurrence ? await spawnNextOccurrence(source, task, task.completedAt ?? new Date()) : null
    requestDoneMoment(task, 'list', count)
    if (successor) showComebackToast([successor], [id], t, i18n.language ?? 'de', new Date())
    rememberUndo(async () => {
      // Schon wieder offen oder gelöscht: nichts mehr zurückzunehmen.
      const row = await source.getSubtask(id)
      if (!row || row.deletedAt || !row.done) return false
      await toggleRef.current(id)
      return true
    }, t('undo.restored.completed'))
    return successor
  }, [buffered, source, t, i18n.language])
  toggleRef.current = toggle

  const rename = useCallback(async (id: string, title: string) => {
    const trimmed = title.trim()
    if (buffered) {
      setBuffer(prev => prev.map(s => (s.id === id ? { ...s, title: trimmed } : s)))
      return
    }
    await source.updateSubtask(id, { title: trimmed })
  }, [buffered, source])

  const insertAfter = useCallback(async (afterId: string, title: string) => {
    const trimmed = title.trim()
    if (!trimmed) return undefined
    if (buffered) {
      const id = crypto.randomUUID()
      setBuffer(prev => {
        const index = prev.findIndex(s => s.id === afterId)
        const item = { id, title: trimmed, done: false }
        if (index === -1) return [...prev, item]
        const next = [...prev]
        next.splice(index + 1, 0, item)
        return next
      })
      return id
    }
    // Auf derselben Ebene wie die Zeile, hinter der sie entsteht (#181).
    const parentId = items.find(item => item.id === afterId)?.parentId ?? taskId!
    const created = await source.insertSubtaskAfter(parentId, afterId, trimmed)
    return created.id
  }, [buffered, taskId, source, items])

  const move = useCallback(async (id: string, afterId: string | null) => {
    if (buffered) {
      setBuffer(prev => {
        const moved = prev.find(s => s.id === id)
        if (!moved) return prev
        const rest = prev.filter(s => s.id !== id)
        const index = afterId === null ? 0 : rest.findIndex(s => s.id === afterId) + 1
        if (afterId !== null && index === 0) return prev
        rest.splice(index, 0, moved)
        return rest
      })
      return
    }
    await source.moveSubtask(id, afterId)
  }, [buffered, source])

  /**
   * Wo eine entfernte Zeile stand — nur für den Puffer nötig. Eine gespeicherte
   * Zeile ist bloß weggeblendet und trägt ihren `sortOrder` weiter mit sich.
   */
  const removedAt = useRef(new Map<string, number>())
  /** Was das Löschen einer gespeicherten Zeile mitnahm (ihr Ast, #181) — für den Rückweg. */
  const removedCascades = useRef(new Map<string, TaskCascade>())

  const remove = useCallback(async (id: string): Promise<SubtaskItem | undefined> => {
    const removed = items.find(s => s.id === id)
    if (!removed) return undefined
    if (buffered) {
      removedAt.current.set(id, items.findIndex(s => s.id === id))
      setBuffer(prev => prev.filter(s => s.id !== id))
      return removed
    }
    removedCascades.current.set(id, await source.deleteSubtask(id))
    return removed
  }, [buffered, items, source])

  const restore = useCallback(async (item: SubtaskItem) => {
    if (buffered) {
      const index = removedAt.current.get(item.id) ?? Number.MAX_SAFE_INTEGER
      setBuffer(prev => {
        if (prev.some(s => s.id === item.id)) return prev
        const next = [...prev]
        next.splice(Math.min(index, next.length), 0, item)
        return next
      })
      removedAt.current.delete(item.id)
      return
    }
    await source.restoreSubtask(item.id, removedCascades.current.get(item.id))
    removedCascades.current.delete(item.id)
  }, [buffered, source])

  const update = useCallback(async (ids: string[], changes: SubtaskChanges) => {
    if (buffered || ids.length === 0) return
    await source.updateTasks(ids.map(id => ({ id, changes })))
  }, [buffered, source])

  const duplicate = useCallback(async (ids: string[]): Promise<string[]> => {
    if (buffered) return []
    const copies: string[] = []
    for (const id of ids) {
      const original = await source.getTask(id)
      if (!original) continue
      // Erst ans Ende, dann hinter die Vorlage: Auf ihrem Wert zu starten, nummerierte die Checkliste neu.
      const copy = await duplicateTask(source, original, { order: undefined })
      await source.moveSubtask(copy.id, id)
      copies.push(copy.id)
    }
    return copies
  }, [buffered, source])

  /** Einrücken und Ausrücken teilen sich den Weg: schreiben, `⌘Z` merken, sonst sagen, warum nicht. */
  const changeLevel = useCallback(async (ids: string[], direction: 'indent' | 'outdent'): Promise<boolean> => {
    if (buffered || ids.length === 0) return false
    const outcome = direction === 'indent' ? await source.indentSubtasks(ids) : await source.outdentSubtasks(ids)
    if ('refused' in outcome) {
      showInfoToast(t(`taskItem.subtaskTree.refused.${outcome.refused}`))
      return false
    }
    if (outcome.before.length === 0) return false
    const title = items.find(item => item.id === outcome.before[0]!.id)?.title ?? ''
    rememberUndo(
      () => source.restorePlacements(outcome.before),
      outcome.before.length === 1
        ? t(`undo.restored.${direction}ed`, { title })
        : t(`undo.restored.${direction}edMultiple`, { count: outcome.before.length }),
    )
    return true
  }, [buffered, source, t, items])
  const indent = useCallback((ids: string[]) => changeLevel(ids, 'indent'), [changeLevel])
  const outdent = useCallback((ids: string[]) => changeLevel(ids, 'outdent'), [changeLevel])

  const drafts = useMemo<SubtaskDraft[]>(
    () => (buffered ? buffer.map(({ title, done }) => ({ title, done })) : []),
    [buffered, buffer],
  )

  return {
    items, loaded: buffered || stored !== undefined, drafts, progress,
    add, insertAfter, move, toggle, rename, remove, restore, update, indent, outdent, duplicate,
  }
}
